import type { AssetDetail } from "@/entities/asset";
import type { InterventionDetail } from "@/entities/intervention";
import type { TabViewItem } from "@/ui/tab-view";

import { useTranslation } from "react-i18next";

import { TAB_KEYS } from "@/constants/TabKeys";
import { InterventionSectionSaveBar } from "@/modules/intervention/intervention-section-save-bar";
import InterventionTabDetails from "@/modules/intervention/intervention-tab-details";
import InterventionTabModules from "@/modules/intervention/intervention-tab-modules";
import { TabView } from "@/ui/tab-view";

import TabObservation from "./TabObservation";
import TabRecommendations from "./TabRecommendedInterventions";

type InterventionDetailsTabsProps = {
  intervention: InterventionDetail;
  interventionAsset?: AssetDetail;
  loading?: boolean;
};

const InterventionDetailsTabs = ({
  intervention,
  interventionAsset,
  loading = false,
}: InterventionDetailsTabsProps) => {
  const { t } = useTranslation();

  if (!intervention) return null;

  const tabs: TabViewItem[] = [
    {
      key: TAB_KEYS.INTERVENTIONS_DETAILS,
      label: t("intervention:details.label"),
      children: (
        <InterventionTabDetails
          interventionAsset={interventionAsset}
          intervention={intervention}
        />
      ),
    },
    {
      key: TAB_KEYS.INTERVENTIONS_OBSERVATIONS,
      label: t("intervention:observations.label"),
      children: <TabObservation interventionId={intervention.id} />,
    },
    {
      key: TAB_KEYS.INTERVENTIONS_RECOMMENDATIONS,
      label: t("intervention:recommendedInterventions.labelWithCount", {
        count: intervention.recommended_interventions_count ?? 0,
      }),
      children: <TabRecommendations interventionId={intervention.id} />,
    },
    {
      key: TAB_KEYS.INTERVENTIONS_MODULES,
      label: t("intervention:modules.label"),
      children: <InterventionTabModules intervention={intervention} />,
    },
  ];

  return (
    <TabView
      isNav
      key={`intervention-${intervention.id}-tabs`}
      tabs={tabs}
      alwaysSyncSearchParams // Keep it to ensure save bar is displayed
      shouldSync={(searchParams) => !searchParams.size} // Only sync when missing tab
      defaultActiveKey={undefined}
      tabBarExtraContent={
        <InterventionSectionSaveBar
          loading={loading}
          intervention={intervention}
        />
      }
    />
  );
};

export default InterventionDetailsTabs;
